import { contacts } from '@/data/contacts';
import { Button } from '@/shared/ui/button';
import { ArrowRight, Building2, Mail, MapPin, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import ProfileImage from '/assets/images/profile/IMG_3079.jpg';

interface PersonalInfoSidebarProps {
  showQuickLinks?: boolean;
  quickLinks?: {
    text: string;
    href: string;
    icon?: React.ComponentType<{ className?: string }>;
  }[];
}

export default function PersonalInfoSidebar({
  showQuickLinks = true,
  quickLinks
}: PersonalInfoSidebarProps) {
  const { t } = useLanguage();

  const defaultQuickLinks = [
    { text: t('about'), href: "/", icon: ArrowRight },
    { text: t('project'), href: "/project", icon: ArrowRight },
    { text: t('posts'), href: "/posts", icon: ArrowRight },
    { text: t('cv'), href: "/cv", icon: ArrowRight }
  ];

  const finalQuickLinks = quickLinks || defaultQuickLinks;

  return (
    <div className="lg:col-span-1">
      <div className="sticky top-24">
        <div className="space-y-8">
          <div className="flex flex-col items-center text-center">
            <div className="w-24 h-24 rounded-full overflow-hidden mb-4 ring-2 ring-border/50">
              <img
                src={ProfileImage}
                alt={t('name')}
                className="w-full h-full object-cover"
              />
            </div>

            <h2 className="text-lg font-medium text-foreground">{t('name')}</h2>

            <div className="mt-2 text-center">
              <p className="text-sm text-muted-foreground">{t('footerAffiliation')}</p>
              <p className="text-sm text-muted-foreground mt-1">{t('footerDegree')}</p>
            </div>
          </div>

          {/* 联系信息 - 容器居中，内容左对齐 */}
          <div className="flex justify-center">
            <div className="space-y-3 text-sm max-w-[240px] w-full">
              <div className="flex items-center gap-3 text-muted-foreground">
                <Mail className="w-4 h-4 flex-shrink-0" />
                <a href={contacts.emailHref} className="hover:text-foreground transition-colors break-all">
                  {contacts.email}
                </a>
              </div>
              <div className="flex items-center gap-3 text-muted-foreground">
                <MapPin className="w-4 h-4 flex-shrink-0" />
                <span>London, UK</span>
              </div>
              <div className="flex items-center gap-3 text-muted-foreground">
                <Clock className="w-4 h-4 flex-shrink-0" />
                <span>UTC+1</span>
              </div>
            </div>
          </div>

          {/* 社交媒体链接 */}
          <div className="flex justify-center space-x-4 pb-8 border-b border-border/50">
            <Button variant="ghost" size="icon" asChild className="h-8 w-8 p-0 hover:bg-muted">
              <a href={contacts.github} target="_blank" rel="noopener noreferrer" aria-label="GitHub">
                <svg className="w-4 h-4 text-muted-foreground hover:text-foreground transition-colors" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                </svg>
              </a>
            </Button>
            <Button variant="ghost" size="icon" asChild className="h-8 w-8 p-0 hover:bg-muted">
              <a href={contacts.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                <svg className="w-4 h-4 text-muted-foreground hover:text-foreground transition-colors" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
            </Button>
            <Button variant="ghost" size="icon" asChild className="h-8 w-8 p-0 hover:bg-muted">
              <a href={contacts.twitter} target="_blank" rel="noopener noreferrer" aria-label="Twitter">
                <svg className="w-4 h-4 text-muted-foreground hover:text-foreground transition-colors" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
              </a>
            </Button>
            <Button variant="ghost" size="icon" asChild className="h-8 w-8 p-0 hover:bg-muted">
              <a href={contacts.bluesky} target="_blank" rel="noopener noreferrer" aria-label="Bluesky">
                <svg className="w-4 h-4 text-muted-foreground hover:text-foreground transition-colors" viewBox="0 0 64 57" fill="none">
                  <path fill="currentColor" d="M13.873 3.805C21.21 9.332 29.103 20.537 32 26.55v15.882c0-.338-.13.044-.41.867-1.512 4.456-7.418 21.847-20.923 7.944-7.111-7.32-3.819-14.64 9.125-16.85-7.405 1.264-15.73-.825-18.014-9.015C1.12 23.022 0 8.51 0 6.55 0-3.268 8.579-.182 13.873 3.805ZM50.127 3.805C42.79 9.332 34.897 20.537 32 26.55v15.882c0-.338.13.044.41.867 1.512 4.456 7.418 21.847 20.923 7.944 7.111-7.32 3.819-14.64-9.125-16.85 7.405 1.264 15.73-.825 18.014-9.015C62.88 23.022 64 8.51 64 6.55c0-9.818-8.578-6.732-13.873-2.745Z"></path>
                </svg>
              </a>
            </Button>
          </div>

          {showQuickLinks && (
            <>
              {/* 桌面端：竖排布局 */}
              <div className="hidden lg:block space-y-2">
                {finalQuickLinks.map((link, index) => (
                  <Button key={index} variant="ghost" className="w-full justify-between text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 px-3 py-2" asChild>
                    <Link to={link.href}>
                      {link.text}
                      {link.icon && <link.icon className="w-3 h-3" />}
                    </Link>
                  </Button>
                ))}
              </div>

              {/* 移动端：优雅的卡片式设计 */}
              <div className="lg:hidden">
                <div className="grid grid-cols-2 gap-3">
                  {finalQuickLinks.map((link, index) => (
                    <Link
                      key={index}
                      to={link.href}
                      className="group relative overflow-hidden rounded-xl bg-muted/30 hover:bg-muted/50 transition-all duration-300 p-4 border border-border/30 hover:border-border/50"
                    >
                      <div className="flex flex-col items-start space-y-2">
                        <div className="flex items-center justify-between w-full">
                          <span className="text-xs font-medium text-foreground group-hover:text-primary transition-colors">
                            {link.text}
                          </span>
                          {link.icon && (
                            <link.icon className="w-3.5 h-3.5 text-muted-foreground group-hover:text-primary transition-all duration-300 group-hover:translate-x-0.5" />
                          )}
                        </div>
                        <div className="h-0.5 w-8 bg-primary/20 group-hover:w-full group-hover:bg-primary/40 transition-all duration-300" />
                      </div>
                    </Link>
                  ))}
                </div>
                
                {/* 或者使用更简洁的按钮式设计 */}
                {/* <div className="space-y-2">
                  {finalQuickLinks.map((link, index) => (
                    <Link
                      key={index}
                      to={link.href}
                      className="flex items-center justify-between w-full px-4 py-3 rounded-lg bg-muted/20 hover:bg-muted/40 transition-all duration-300 group"
                    >
                      <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                        {link.text}
                      </span>
                      {link.icon && (
                        <link.icon className="w-4 h-4 text-muted-foreground/50 group-hover:text-primary transition-all duration-300 group-hover:translate-x-1" />
                      )}
                    </Link>
                  ))}
                </div> */}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
